import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { SignInLayout, siblingOrigin } from "@vd/ui";
import { ChevronLeft, KeyRound } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { errorMessage, setCsrfToken } from "../api/client";
import { auth, qk } from "../api/ops";
import type { OpsMe } from "../api/types";
import { cancelled, getPasskey, passkeysSupported } from "../auth/passkey";
import { Button, Input } from "../components/ui";
import { DEV_SIGNIN_ENABLED } from "../config";
import { startHandoff } from "../lib/handoff";
import { useNavigate } from "../lib/navigate";
import { safeReturnTo } from "../lib/returnTo";

const search = z.object({
  redirect: z.string().optional().catch(undefined),
  error: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/signin")({
  staticData: { crumb: { label: "Sign in", parents: [] } },
  validateSearch: (s) => search.parse(s),
  component: SignIn,
});

/**
 * "Back to Admin" (or App) when the mode switch brought the person here: the customer app's
 * origin rides in the resume address (`back`), and only a sibling Kite host is linked.
 */
export function backLink(redirect: string | undefined, here: string): { href: string; label: string } | null {
  if (!redirect?.startsWith("/resume")) return null;
  const q = new URLSearchParams(redirect.split("?")[1] ?? "");
  const origin = siblingOrigin(q.get("back"), here);
  const from = q.get("from");
  if (!origin || (from !== "app" && from !== "admin")) return null;
  return { href: `${origin}/${from}/resume`, label: from === "app" ? "Back to App" : "Back to Admin" };
}

function SignIn() {
  const { redirect, error: initialError } = Route.useSearch();
  const back = backLink(redirect, window.location.href);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [busy, setBusy] = useState<"passkey" | "dev" | "kite" | null>(null);
  const [email, setEmail] = useState("apino@vertec.io");

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  const done = async (me: OpsMe) => {
    setCsrfToken(me.csrf_token);
    qc.setQueryData(qk.me, me);
    await navigate({ to: safeReturnTo(redirect) });
  };

  const passkey = async () => {
    setBusy("passkey");
    setError(null);
    try {
      const { public_key } = await auth.passkeyOptions();
      const credential = await getPasskey(public_key);
      await done(await auth.passkey(credential));
    } catch (e) {
      // Closing the browser's prompt needs no message.
      if (!cancelled(e)) setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  // Staff without a passkey: a one-time handoff from their Kite account (migration 0203).
  const kite = async () => {
    setBusy("kite");
    setError(null);
    try {
      const target = safeReturnTo(redirect);
      await startHandoff(target.startsWith("/resume") ? null : target);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(null);
    }
  };

  const dev = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy("dev");
    setError(null);
    try {
      await done(await auth.dev(email.trim()));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <SignInLayout
      badge="Platform"
      footer={
        DEV_SIGNIN_ENABLED ? (
          <form onSubmit={dev} className="flex flex-col gap-3">
            <Input
              aria-label="Staff email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" busy={busy === "dev"} disabled={busy !== null}>
              Sign in for development
            </Button>
          </form>
        ) : undefined
      }
    >
      <h1 className="font-display font-medium text-display">Sign in</h1>
      <Button
        variant="primary"
        className="mt-6 w-full"
        onClick={passkey}
        busy={busy === "passkey"}
        disabled={!passkeysSupported() || busy !== null}
      >
        <KeyRound className="size-4" />
        Sign in with a passkey
      </Button>
      <Button className="mt-3 w-full" onClick={kite} busy={busy === "kite"} disabled={busy !== null}>
        Continue with your Kite account
      </Button>
      {error ? (
        <p role="alert" className="mt-3 rounded-lg bg-danger-soft px-3 py-2 text-danger-ink text-ui">
          {error}
        </p>
      ) : null}
      {back ? (
        <a
          href={back.href}
          className="mt-4 inline-flex h-8 items-center gap-1 self-center rounded-pill px-3 font-medium text-ink-2 text-ui hover:bg-fill-2 hover:text-ink"
        >
          <ChevronLeft className="size-4" aria-hidden />
          {back.label}
        </a>
      ) : null}
    </SignInLayout>
  );
}
